import { useState, useEffect } from 'react';
import { Save, Loader as Loader2 } from 'lucide-react';
import { useApi } from '../../lib/useApi';
import type { TerritorySegmentConfig as SegmentConfigType } from '../../types';

interface Props {
  config: SegmentConfigType | null;
  orgId: string;
  userId: string;
  onSaved: () => void;
}

const SEGMENT_TYPES = [
  { value: 'smb', label: 'SMB' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'mid_market', label: 'Mid-Market' },
  { value: 'enterprise', label: 'Enterprise' },
  { value: 'strategic', label: 'Strategic' },
] as const;

const SECTORS = [
  { value: 'public', label: 'Public' },
  { value: 'private', label: 'Private' },
  { value: 'both', label: 'Both' },
] as const;

export default function TerritorySegmentConfig({ config, orgId, userId, onSaved }: Props) {
  const { apiPut } = useApi();
  const [segmentName, setSegmentName] = useState(config?.segment_name || '');
  const [segmentType, setSegmentType] = useState<SegmentConfigType['segment_type']>(config?.segment_type || 'enterprise');
  const [sector, setSector] = useState<SegmentConfigType['sector']>(config?.sector || 'both');
  const [minEmployees, setMinEmployees] = useState<string>(config?.min_employees?.toString() || '');
  const [maxEmployees, setMaxEmployees] = useState<string>(config?.max_employees?.toString() || '');
  const [minRevenue, setMinRevenue] = useState<string>(config?.min_revenue?.toString() || '');
  const [maxRevenue, setMaxRevenue] = useState<string>(config?.max_revenue?.toString() || '');
  const [geos, setGeos] = useState<string>(config?.geos?.join(', ') || '');
  const [verticals, setVerticals] = useState<string>(config?.verticals?.join(', ') || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!config) return;
    setSegmentName(config.segment_name || '');
    setSegmentType(config.segment_type);
    setSector(config.sector);
    setMinEmployees(config.min_employees?.toString() || '');
    setMaxEmployees(config.max_employees?.toString() || '');
    setMinRevenue(config.min_revenue?.toString() || '');
    setMaxRevenue(config.max_revenue?.toString() || '');
    setGeos(config.geos?.join(', ') || '');
    setVerticals(config.verticals?.join(', ') || '');
  }, [config]);

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      org_id: orgId,
      user_id: userId,
      segment_name: segmentName || `${segmentType} segment`,
      segment_type: segmentType,
      sector,
      min_employees: minEmployees ? parseInt(minEmployees) : null,
      max_employees: maxEmployees ? parseInt(maxEmployees) : null,
      min_revenue: minRevenue ? parseFloat(minRevenue) : null,
      max_revenue: maxRevenue ? parseFloat(maxRevenue) : null,
      geos: geos.split(',').map(g => g.trim()).filter(Boolean),
      verticals: verticals.split(',').map(v => v.trim()).filter(Boolean),
      is_public_company: sector === 'public' ? true : sector === 'private' ? false : null,
      custom_filters: {},
      updated_at: new Date().toISOString(),
    };

    await apiPut('/v1/territory/segment-config', payload);
    setSaving(false);
    onSaved();
  };

  const inputClass = "w-full border border-app-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/30 focus:border-forest-500";

  return (
    <div className="bg-app-surface rounded-lg border border-app-line p-6">
      <h3 className="text-sm font-semibold text-app-ink mb-4">Territory Segment Configuration</h3>
      <p className="text-xs text-app-muted mb-5">Define the ICP parameters for your team's territory. This influences account suggestions and alignment scoring.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-medium text-app-muted mb-1">Segment Name</label>
          <input type="text" value={segmentName} onChange={e => setSegmentName(e.target.value)} placeholder="e.g., EMEA Enterprise" className={inputClass} />
        </div>
        <div>
          <label className="block text-xs font-medium text-app-muted mb-1">Segment Type</label>
          <select value={segmentType} onChange={e => setSegmentType(e.target.value as SegmentConfigType['segment_type'])} className={inputClass}>
            {SEGMENT_TYPES.map(st => <option key={st.value} value={st.value}>{st.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-app-muted mb-1">Sector</label>
          <select value={sector} onChange={e => setSector(e.target.value as SegmentConfigType['sector'])} className={inputClass}>
            {SECTORS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-app-muted mb-1">Min Employees</label>
          <input type="number" value={minEmployees} onChange={e => setMinEmployees(e.target.value)} placeholder="e.g., 500" className={inputClass} />
        </div>
        <div>
          <label className="block text-xs font-medium text-app-muted mb-1">Max Employees</label>
          <input type="number" value={maxEmployees} onChange={e => setMaxEmployees(e.target.value)} placeholder="e.g., 10000" className={inputClass} />
        </div>
        <div>
          <label className="block text-xs font-medium text-app-muted mb-1">Min Revenue ($)</label>
          <input type="number" value={minRevenue} onChange={e => setMinRevenue(e.target.value)} placeholder="e.g., 10000000" className={inputClass} />
        </div>
        <div>
          <label className="block text-xs font-medium text-app-muted mb-1">Max Revenue ($)</label>
          <input type="number" value={maxRevenue} onChange={e => setMaxRevenue(e.target.value)} placeholder="e.g., 1000000000" className={inputClass} />
        </div>
        <div>
          <label className="block text-xs font-medium text-app-muted mb-1">Geographies</label>
          <input type="text" value={geos} onChange={e => setGeos(e.target.value)} placeholder="e.g., UK, Germany, France" className={inputClass} />
          <p className="text-[10px] text-app-muted mt-0.5">Comma-separated</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-app-muted mb-1">Industry Verticals</label>
          <input type="text" value={verticals} onChange={e => setVerticals(e.target.value)} placeholder="e.g., Financial Services, Healthcare" className={inputClass} />
          <p className="text-[10px] text-app-muted mt-0.5">Comma-separated</p>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-forest-600 rounded-lg hover:bg-forest-700 transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Configuration
        </button>
      </div>
    </div>
  );
}
